import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Activity, Users, LogIn, ShieldAlert, Timer, Globe, HardDrive, Mail, Loader2, PlayCircle, Cpu, Siren, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

const Stat = ({ label, value, icon: Icon, color = "#3A2DB8", testId }) => (
  <div className="card-soft p-4" data-testid={testId}>
    <div className="flex items-center justify-between mb-1"><span className="text-[11px] text-secondary">{label}</span><Icon className="w-4 h-4" style={{ color }} /></div>
    <div className="font-heading font-bold text-2xl" style={{ color }}>{value}</div>
  </div>
);

const Health = ({ label, ok, detail, icon: Icon, testId }) => (
  <div className="flex items-center gap-3 border border-border rounded-lg p-3" data-testid={testId}>
    <Icon className="w-5 h-5 text-[#7C3AED]" />
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium text-[#1E1560]">{label}</div>
      <div className="text-[11px] text-secondary truncate">{detail}</div>
    </div>
    <Badge className={`rounded-full ${ok ? "bg-[#0F9D77]/15 text-[#0F9D77]" : "bg-[#FF8A3D]/15 text-[#B45309]"}`}>{ok ? "OK" : "Attention"}</Badge>
  </div>
);

export default function AdminMonitor() {
  const [m, setM] = useState(null);
  const navigate = useNavigate();
  const load = useCallback(() => api.get("/admin/monitor").then((r) => setM(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  const ack = async (id) => { await api.post(`/admin/alerts/${id}/ack`); load(); };

  if (!m) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#3A2DB8]" /></div>;
  const d = m.last_24h;

  return (
    <div className="space-y-6" data-testid="admin-monitor">
      <div className="flex items-center justify-between">
        <p className="overline text-[#7C3AED]">Live · last 24 hours · refreshes every 10s</p>
        <span className="text-[11px] font-mono text-secondary">{m.generated_at?.slice(11, 19)} UTC</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <Stat label="Logins" value={d.logins} icon={LogIn} color="#0F9D77" testId="mon-logins" />
        <Stat label="Failed logins" value={d.failed_logins} icon={ShieldAlert} color={d.failed_logins ? "#E5325F" : "#3A2DB8"} testId="mon-failed-logins" />
        <Stat label="Registrations" value={d.registrations} icon={Users} testId="mon-registrations" />
        <Stat label="Runs started" value={d.runs_started} icon={PlayCircle} testId="mon-runs-started" />
        <Stat label="Runs completed" value={d.runs_completed} icon={Activity} color="#0F9D77" testId="mon-runs-completed" />
        <Stat label="Runs failed" value={d.runs_failed} icon={ShieldAlert} color={d.runs_failed ? "#E5325F" : "#3A2DB8"} testId="mon-runs-failed" />
        <Stat label="API errors" value={d.api_errors} icon={ShieldAlert} color={d.api_errors ? "#FF8A3D" : "#3A2DB8"} testId="mon-api-errors" />
        <Stat label="Checkouts" value={d.checkouts} icon={Timer} testId="mon-checkouts" />
      </div>

      <div className="card-soft" data-testid="alerts-panel">
        <div className="p-4 border-b border-border flex items-center gap-2">
          <Siren className="w-4 h-4 text-[#E5325F]" /><span className="font-heading font-semibold text-[#1E1560]">Open alerts ({m.alerts?.length || 0})</span>
          <span className="ml-auto text-[11px] text-secondary">Rules: failed-login spike · 2 consecutive failing runs · run blocked · scheduler stalled</span>
        </div>
        {(m.alerts || []).length === 0 ? <p className="p-4 text-sm text-secondary" data-testid="alerts-empty">No open alerts.</p> : (
          <div className="divide-y divide-border">
            {m.alerts.map((a) => (
              <div key={a.id} className="p-3 flex items-start gap-3" data-testid={`alert-${a.id}`}>
                <Badge className="rounded-full bg-[#E5325F]/15 text-[#E5325F] text-[10px] shrink-0">{a.rule}</Badge>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#1E1560]">{a.title}</div>
                  <div className="text-xs text-secondary">{a.body} · {a.created_at?.slice(0, 16).replace("T", " ")} UTC</div>
                </div>
                {a.meta?.run_id && <button onClick={() => navigate(`/runs/${a.meta.run_id}`)} className="text-xs text-[#3A2DB8] hover:text-[#FF6B3D]">Open run</button>}
                <Button size="sm" variant="ghost" onClick={() => ack(a.id)} data-testid={`alert-ack-${a.id}`} className="rounded-full h-7 gap-1 text-[#0F9D77] hover:bg-[#0F9D77]/10"><Check className="w-3.5 h-3.5" /> Ack</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Health label="Scheduler" ok={m.scheduler.healthy} icon={Timer} testId="health-scheduler"
          detail={m.scheduler.last_tick ? `Last tick ${m.scheduler.last_tick.slice(11, 19)} UTC · ${m.scheduler.mode || m.scheduler.mode_setting}` : "No tick recorded yet"} />
        <Health label="Browser engine" ok={m.browser.mode === "remote" || m.browser.local_available} icon={Globe} testId="health-browser"
          detail={m.browser.mode === "remote" ? `Remote Chrome · ${m.browser.endpoint_host}` : "Local Chromium (preview only — set BROWSER_WS_ENDPOINT for production)"} />
        <Health label="Evidence storage" ok={m.storage.configured} icon={HardDrive} testId="health-storage"
          detail={m.storage.configured ? "Emergent Object Store — durable across redeploys" : m.storage.error || "Local disk only"} />
        <Health label="Email pipeline" ok={m.email.configured} icon={Mail} testId="health-email"
          detail={m.email.configured ? `Resend · ${m.email.sender}` : "Not configured — reports in-app only"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-soft" data-testid="active-runs-panel">
          <div className="p-4 border-b border-border flex items-center gap-2"><Cpu className="w-4 h-4 text-[#7C3AED]" /><span className="font-heading font-semibold text-[#1E1560]">Active runs ({m.active_runs.length})</span></div>
          {m.active_runs.length === 0 ? <p className="p-5 text-sm text-secondary">No agents running right now.</p> : (
            <div className="divide-y divide-border">
              {m.active_runs.map((r) => (
                <button key={r.id} onClick={() => navigate(`/runs/${r.id}`)} className="w-full text-left p-4 hover:bg-muted/60 flex items-center gap-3" data-testid={`active-run-${r.id}`}>
                  <Loader2 className="w-4 h-4 animate-spin text-[#7C3AED]" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[#1E1560] truncate">{r.environment_name} <span className="text-secondary">· {r.customer_email}</span></div>
                    <div className="text-[11px] text-secondary truncate">{r.progress?.current}/{r.progress?.total} · {r.progress?.script}</div>
                  </div>
                  <Badge className="rounded-full bg-[#7C3AED]/10 text-[#7C3AED] text-[10px]">{r.trigger}</Badge>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="card-soft" data-testid="recent-errors-panel">
          <div className="p-4 border-b border-border flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-[#E5325F]" /><span className="font-heading font-semibold text-[#1E1560]">Recent warnings &amp; errors</span></div>
          {m.recent_errors.length === 0 ? <p className="p-5 text-sm text-secondary">Nothing to report.</p> : (
            <div className="divide-y divide-border max-h-80 overflow-y-auto">
              {m.recent_errors.map((e) => (
                <div key={e.id} className="p-3 text-xs" data-testid={`recent-error-${e.id}`}>
                  <div className="flex items-center gap-2">
                    <Badge className={`rounded-full text-[10px] ${e.level === "error" ? "bg-[#E5325F]/15 text-[#E5325F]" : "bg-[#FF8A3D]/15 text-[#B45309]"}`}>{e.kind}</Badge>
                    <span className="text-secondary font-mono">{e.ts?.slice(0, 19).replace("T", " ")}</span>
                    {e.email && <span className="text-[#1E1560] truncate">{e.email}</span>}
                  </div>
                  <div className="text-secondary mt-1 font-mono truncate">{JSON.stringify(e.meta)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <p className="text-[11px] text-secondary">Totals: {m.totals.customers} customers · {m.totals.environments} environments · {m.totals.runs} runs · {m.totals.audit_events} audit events</p>
    </div>
  );
}
